import { ChangeEvent, FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { showToast } from "@/helpers/toast"
import { generateUuid } from "@/helpers/uuid"
import { useModal, useModals } from "@/modals/hooks"
import {
  useDiscardItemMutation,
  useGetInventoryQuery
} from "@/store/apis/inventoryAPI"
import { IDiscardReq } from "@/store/apis/inventoryAPI/inventory.types"
import { Modal } from "@/ui/Modal"
import styles from "./DiscardConfirmModal.module.scss"
import { clampDiscardQuantity, classifyDiscardFailure } from "./helpers"

const modalType = "inventoryDiscardConfirm"

export const DiscardConfirmModal: FC = () => {
  const { t } = useTranslation("inventory")
  const { modal, getModalParams } = useModal(modalType)
  const { closeModal } = useModals()
  const instanceId = getModalParams(modalType)?.instanceId

  const { item, isSuccess, isFetching } = useGetInventoryQuery(undefined, {
    selectFromResult: ({ data, isSuccess, isFetching }) => ({
      item: data?.items.find((i) => i.id === instanceId) ?? null,
      isSuccess,
      isFetching
    })
  })

  const [discardItem, discardState] = useDiscardItemMutation()
  const busy = discardState.isLoading

  const [inputValue, setInputValue] = useState("1")
  const [pending, setPending] = useState<IDiscardReq | null>(null)
  const [awaitingSettle, setAwaitingSettle] = useState(false)
  const wasFetching = useRef(false)

  const max = item?.quantity ?? 1
  const parsed = /^\d+$/.test(inputValue) ? Number(inputValue) : Number.NaN
  const isValidQuantity = parsed >= 1 && parsed <= max

  const closeBoth = () => {
    closeModal("inventoryDiscardConfirm")
    closeModal("inventoryItem")
  }

  /* Vanished-item rule (spec §4.5) — takes precedence over the retry state. */
  useEffect(() => {
    if (isSuccess && !item && !busy) {
      showToast.info(t("discard.removedToast"))
      closeBoth()
    }
  }, [isSuccess, item, busy])

  /* Settle gate: clear only on an observed fetching → not-fetching transition,
     never on the raw flag (spec §4.5). */
  useEffect(() => {
    if (awaitingSettle && wasFetching.current && !isFetching) {
      setAwaitingSettle(false)
    }
    wasFetching.current = isFetching
  }, [isFetching, awaitingSettle])

  /* Reactive clamp — only while no pending action is held. Deliberately keyed
     to the stack size alone; react-hooks/exhaustive-deps is OFF repo-wide, so
     the partial dep array needs (and must not have) a disable directive — an
     unused directive is itself a warning that fails the pre-commit gate. */
  useEffect(() => {
    if (
      !pending &&
      item &&
      /^\d+$/.test(inputValue) &&
      parsed > item.quantity
    ) {
      setInputValue(String(item.quantity))
    }
  }, [item?.quantity])

  if (!item) {
    return <Modal.Center type={modalType} className={styles.raise} />
  }

  const editQuantity = (next: string) => {
    setPending(null)
    setInputValue(next)
  }

  const handleInput = (e: ChangeEvent<HTMLInputElement>) =>
    editQuantity(e.target.value)

  const handleBlur = () =>
    setInputValue(String(clampDiscardQuantity(Number(inputValue), max)))

  const step = (delta: number) =>
    editQuantity(
      String(
        clampDiscardQuantity(clampDiscardQuantity(parsed, max) + delta, max)
      )
    )

  const handleConfirm = async () => {
    const request = pending ?? {
      instanceId: item.id,
      quantity: clampDiscardQuantity(parsed, max),
      idempotencyKey: generateUuid()
    }
    setPending(request)
    try {
      await discardItem(request).unwrap()
      setPending(null)
      showToast.success(
        t("discard.successToast", {
          amount: request.quantity,
          name: t(`item.${item.definitionCode}.name`)
        })
      )
      closeBoth()
    } catch (error) {
      const handling = classifyDiscardFailure(error)
      if (!handling.keepPending) {
        setPending(null)
      }
      showToast.error(t(handling.toastKey))
      setAwaitingSettle(true)
      if (handling.outcome === "closeAll") {
        closeBoth()
      } else if (handling.outcome === "closeConfirm") {
        modal.close()
      }
    }
  }

  const confirmDisabled =
    busy || awaitingSettle || (!pending && !isValidQuantity)

  return (
    <Modal.Center type={modalType} className={styles.raise} isCanClose={!busy}>
      <div className={styles.DiscardConfirmModal}>
        <h3 className={styles.DiscardConfirmModal_title}>
          {t("discard.title", {
            name: t(`item.${item.definitionCode}.name`)
          })}
        </h3>
        {max > 1 && (
          <>
            <div className={styles.DiscardConfirmModal_quantityRow}>
              <span>{t("discard.quantityLabel")}</span>
              <button type="button" disabled={busy} onClick={() => step(-1)}>
                −
              </button>
              <input
                type="text"
                inputMode="numeric"
                value={inputValue}
                disabled={busy}
                onChange={handleInput}
                onBlur={handleBlur}
              />
              <button type="button" disabled={busy} onClick={() => step(1)}>
                +
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => editQuantity(String(max))}
              >
                {t("discard.max")}
              </button>
            </div>
            <p className={styles.DiscardConfirmModal_stackHint}>
              {t("discard.stackHint", { amount: max })}
            </p>
          </>
        )}
        <p className={styles.DiscardConfirmModal_warning}>
          {t("discard.warning")}
        </p>
        <div className={styles.DiscardConfirmModal_actions}>
          <button type="button" disabled={busy} onClick={modal.close}>
            {t("discard.cancel")}
          </button>
          <button
            type="button"
            className={styles.DiscardConfirmModal_confirm}
            disabled={confirmDisabled}
            onClick={handleConfirm}
          >
            {pending ? t("discard.retry") : t("discard.confirm")}
          </button>
        </div>
      </div>
    </Modal.Center>
  )
}
